import React from 'react';
import { BulbOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import clsx from 'clsx';

export type InfoNoteVariant = 'tip' | 'info' | 'warning';

interface InfoNoteProps {
  variant?: InfoNoteVariant;
  /** Tiêu đề ngắn; mặc định theo variant (Mẹo / Thông tin / Lưu ý). */
  title?: string;
  /** Một ghi chú (string/node) hoặc danh sách ghi chú. */
  items: React.ReactNode | React.ReactNode[];
  className?: string;
}

const VARIANT_META: Record<
  InfoNoteVariant,
  { defaultTitle: string; icon: React.ReactNode; container: string; iconColor: string }
> = {
  tip: {
    defaultTitle: 'Mẹo',
    icon: <BulbOutlined />,
    // Không dùng modifier /60: token bind var() nên Tailwind không tính được alpha
    container: 'tw-border-l-primary tw-bg-accent-mint',
    iconColor: 'tw-text-primary',
  },
  info: {
    defaultTitle: 'Thông tin',
    icon: <InfoCircleOutlined />,
    container: 'tw-border-l-secondary tw-bg-surface-container-low',
    iconColor: 'tw-text-secondary',
  },
  warning: {
    defaultTitle: 'Lưu ý',
    icon: <ExclamationCircleOutlined />,
    container: 'tw-border-l-warning tw-bg-[#FFB155]/10',
    iconColor: 'tw-text-warning',
  },
};

/**
 * Khối ghi chú/mẹo dùng chung: viền nhấn bên trái + icon + tiêu đề, thay cho
 * các dòng "💡 ..." trơn khó quét mắt. Một item render thành đoạn văn; nhiều
 * item thành danh sách có bullet.
 */
const InfoNote: React.FC<InfoNoteProps> = ({ variant = 'tip', title, items, className }) => {
  const meta = VARIANT_META[variant];
  const list = Array.isArray(items) ? items : [items];

  return (
    <div
      className={clsx(
        'tw-rounded-lg tw-border-0 tw-border-l-4 tw-border-solid tw-px-3.5 tw-py-2.5',
        meta.container,
        className,
      )}
    >
      <div className={clsx('tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold', meta.iconColor)}>
        {meta.icon}
        <span className="tw-uppercase tw-tracking-wide">{title ?? meta.defaultTitle}</span>
      </div>
      {list.length === 1 ? (
        <p className="tw-m-0 tw-text-[13px] tw-leading-relaxed tw-text-on-surface">{list[0]}</p>
      ) : (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
          {list.map((item, index) => (
            <li key={index} className="tw-flex tw-gap-2 tw-text-[13px] tw-leading-relaxed tw-text-on-surface">
              <span className={clsx('tw-mt-[7px] tw-h-1 tw-w-1 tw-shrink-0 tw-rounded-full tw-bg-current', meta.iconColor)} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default InfoNote;
